


import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../features/notification/notificationSlice';
import jucoLogo from '../../assets/images/Logo_JUCO_header.png';
import MessagesModal from '../modals/MessagesModal';
import { ThemeToggle } from '../common/ThemeToggle';
import { isThemedRoute } from '../../utils/theme';

// Icons (you can replace with your Icons component if needed)
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const DashboardNavbar = ({ userRole = 'player' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const rolePrefix = location.pathname.split('/')[1] || userRole;
  const messagesPath = `/${rolePrefix}/messages`;
  // Pages that follow light/dark get a solid navy bar so the white links stay readable
  const themed = isThemedRoute(location.pathname);

  const { notifications, unreadCount, loading: notificationsLoading } = useSelector((state) => state.notification);
  const showNotificationBell = ['player'].includes(userRole);
  const showMessagesIcon = !['admin', 'superAdmin'].includes(userRole);

  // Navigation items based on user role
  const navItems = {
    player: [
      { label: 'HOME', path: '/player/dashboard' },
      { label: 'SUPPORT', path: '/player/support' }
    ],
    coach: [
      { label: 'HOME', path: '/coach/dashboard' },
      { label: 'SUPPORT', path: '/coach/support' }
    ],
    scout: [
      { label: 'HOME', path: '/scout/dashboard' },
      { label: 'SUPPORT', path: '/scout/support' }
    ],
    admin: [
      { label: 'DASHBOARD', path: '/admin/dashboard' },
      // { label: 'USERS', path: '/admin/users' },
      // { label: 'PLAYERS', path: '/admin/players' },
      // { label: 'COACHES', path: '/admin/coaches' },
      // { label: 'SCOUTS', path: '/admin/scouts' },
      // { label: 'SETTINGS', path: '/admin/settings' }
    ]
  };

  const currentNavItems = navItems[userRole] || navItems.player;

  // Helper function to check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Fetch notifications on mount
  useEffect(() => {
    if (showNotificationBell) {
      dispatch(fetchNotifications({ page: 1, limit: 20, role: userRole }));
    }
  }, [dispatch, showNotificationBell, userRole]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    };

    if (isProfileDropdownOpen || isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen, isNotificationsOpen]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if API fails, clear local storage and redirect
      navigate('/auth/login');
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${themed ? 'bg-jp-band' : 'bg-transparent backdrop-blur-sm'} border-b border-slate-700/30 font-roboto`} style={{ pointerEvents: 'none' }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-0" style={{ pointerEvents: 'auto' }}>
        <div className="flex items-center justify-between h-16 sm:h-20 gap-4 overflow-hidden">

          {/* Left Side: Logo + Navigation */}
          <div className="flex items-center gap-6">
            {/* Logo - trimmed of transparent padding so it fits inside the bar (same as the public top bar) */}
            <Link to="/dashboard" className="flex items-center">
              <img src={jucoLogo} alt="JUCO Pipeline" className="h-10 sm:h-12 lg:h-14 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1"> 
              {currentNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-xs font-medium transition-colors whitespace-nowrap ${
                    isActive(item.path)
                      ? 'text-cyan-400 border-b-2 border-cyan-400'
                      : 'text-white hover:text-cyan-400'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side: Icons */}
          <div className="hidden lg:flex items-center space-x-2">
            {themed && <ThemeToggle className="mr-1" />}
            {/* Shop Button */}
            {/* <a
              href="http://jucopipeline.myshopify.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-cyan-400 to-blue-500 text-white rounded hover:from-cyan-300 hover:to-blue-400 transition-all whitespace-nowrap"
            >
              JUCO Pipeline Shop
            </a> */}

            {/* Notification Bell Icon */}
            {showNotificationBell && (
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className={`p-2 transition-colors relative ${
                    isNotificationsOpen ? 'text-cyan-400' : 'text-white hover:text-cyan-400'
                  }`}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Modal */}
                {isNotificationsOpen && (
                  <>
                    <div
                      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                      onClick={() => setIsNotificationsOpen(false)}
                      style={{ pointerEvents: 'auto' }}
                    />
                    <div
                      className="fixed top-20 right-4 sm:right-8 w-80 sm:w-96 max-h-[70vh] bg-jp-field backdrop-blur-md border border-jp-line rounded-2xl shadow-2xl z-[101] overflow-hidden flex flex-col"
                      style={{ pointerEvents: 'auto' }}
                    >
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-jp-line flex items-center justify-between">
                        <h3 className="text-jp-ink font-semibold text-sm">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={() => dispatch(markAllNotificationsAsRead({ role: userRole }))}
                            className="text-xs text-jp-accent hover:text-jp-accent/80 transition-colors"
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>

                      {/* Notification List */}
                      <div className="overflow-y-auto flex-1">
                        {notificationsLoading && notifications.length === 0 ? (
                          <div className="p-6 text-center text-jp-muted text-sm">Loading...</div>
                        ) : notifications.length === 0 ? (
                          <div className="p-6 text-center text-jp-muted text-sm">No notifications yet</div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification._id}
                              onClick={() => {
                                if (!notification.isRead) {
                                  dispatch(markNotificationAsRead({ notificationId: notification._id, role: userRole }));
                                }
                              }}
                              className={`px-4 py-3 border-b border-jp-line cursor-pointer transition-colors hover:bg-jp-section ${
                                !notification.isRead ? 'bg-jp-section' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Unread dot */}
                                <div className="mt-1.5 flex-shrink-0">
                                  {!notification.isRead && (
                                    <div className="w-2 h-2 rounded-full bg-cyan-400" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-jp-ink font-medium truncate">
                                    {notification.title || notification.type}
                                  </p>
                                  <p className="text-xs text-jp-muted mt-0.5 line-clamp-2">
                                    {notification.message}
                                  </p>
                                  <p className="text-[10px] text-jp-muted/80 mt-1">
                                    {new Date(notification.createdAt).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Messages Icon */}
            {showMessagesIcon && (
              <button
                onClick={() => {
                  if (location.pathname === messagesPath) {
                    // Already on messages page, do nothing
                    return;
                  }
                  setIsMessagesModalOpen(true);
                }}
                className={`p-2 transition-colors ${
                  location.pathname === messagesPath
                    ? 'text-cyan-400'
                    : 'text-white hover:text-cyan-400'
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>
            )}

            {/* Profile Icon with Dropdown */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className={`p-2 transition-colors ${
                  isProfileDropdownOpen
                    ? 'text-cyan-400'
                    : 'text-white hover:text-cyan-400'
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>

              {/* Profile Dropdown Modal */}
              {isProfileDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                    onClick={() => setIsProfileDropdownOpen(false)}
                    style={{ pointerEvents: 'auto' }}
                  />
                  <div
                    className="fixed top-20 right-4 sm:right-8 w-56 bg-jp-field backdrop-blur-md border border-jp-line rounded-2xl shadow-2xl z-[101] overflow-hidden"
                    style={{ pointerEvents: 'auto' }}
                  >
                    {/* User Info */}
                    {user && (
                      <div className="px-4 py-3 border-b border-jp-line">
                        <p className="text-sm font-medium text-jp-ink">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-jp-muted truncate">{user.email}</p>
                      </div>
                    )}

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setIsProfileDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-jp-ink hover:bg-jp-section transition-colors flex items-center space-x-2"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>View Profile</span>
                      </button>

                      {userRole === 'player' && (
                        <button
                          onClick={() => {
                            navigate('/player/edit-profile');
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-jp-ink hover:bg-jp-section transition-colors flex items-center space-x-2"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit Profile</span>
                        </button>
                      )}

                      {userRole === 'coach' && (
                        <button
                          onClick={() => {
                            navigate('/coach/edit-profile');
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-jp-ink hover:bg-jp-section transition-colors flex items-center space-x-2"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit Profile</span>
                        </button>
                      )}

                      {userRole === 'scout' && (
                        <button
                          onClick={() => {
                            navigate('/scout/edit-profile');
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-jp-ink hover:bg-jp-section transition-colors flex items-center space-x-2"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit Profile</span>
                        </button>
                      )}

                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-jp-section transition-colors flex items-center space-x-2"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50" style={{ pointerEvents: 'auto' }}>
          <div className="px-4 py-3 space-y-1">
            {currentNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-cyan-400 bg-slate-800/50 rounded-lg'
                    : 'text-white hover:text-cyan-400'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile Shop Button */}
            {/* <div className="pt-3 border-t border-slate-700 mt-3">
              <a
                href="http://jucopipeline.myshopify.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full px-4 py-2 text-sm font-medium bg-gradient-to-r from-cyan-400 to-blue-500 text-white rounded hover:from-cyan-300 hover:to-blue-400 transition-all text-center mb-3"
              >
                JUCO Pipeline Shop
              </a>
            </div> */}

            {/* Mobile Icons Section */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Mobile Notification Bell */}
              {showNotificationBell && (
                <button
                  onClick={() => {
                    setIsNotificationsOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 text-white hover:text-cyan-400 transition-colors relative"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span className="text-sm">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              )}

              {showMessagesIcon && (
                <button
                  className={`flex items-center space-x-2 transition-colors ${
                    location.pathname === messagesPath
                      ? 'text-cyan-400'
                      : 'text-white hover:text-cyan-400'
                  }`}
                  onClick={() => {
                    if (location.pathname === messagesPath) {
                      setIsMenuOpen(false);
                      return;
                    }
                    setIsMessagesModalOpen(true);
                    setIsMenuOpen(false);
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">Messages</span>
                </button>
              )}

              <button
                onClick={() => { navigate('/profile'); setIsMenuOpen(false); }}
                className={`flex items-center space-x-2 transition-colors ${
                  location.pathname === '/profile'
                    ? 'text-cyan-400'
                    : 'text-white hover:text-cyan-400'
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm">Profile</span>
              </button>

              {userRole === 'player' && (
                <button
                  onClick={() => { navigate('/player/edit-profile'); setIsMenuOpen(false); }}
                  className={`flex items-center space-x-2 transition-colors ${
                    location.pathname === '/player/edit-profile'
                      ? 'text-cyan-400'
                      : 'text-white hover:text-cyan-400'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm">Edit Profile</span>
                </button>
              )}

              {userRole === 'coach' && (
                <button
                  onClick={() => { navigate('/coach/edit-profile'); setIsMenuOpen(false); }}
                  className={`flex items-center space-x-2 transition-colors ${
                    location.pathname === '/coach/edit-profile'
                      ? 'text-cyan-400'
                      : 'text-white hover:text-cyan-400'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm">Edit Profile</span>
                </button>
              )}

              {userRole === 'scout' && (
                <button
                  onClick={() => { navigate('/scout/edit-profile'); setIsMenuOpen(false); }}
                  className={`flex items-center space-x-2 transition-colors ${
                    location.pathname === '/scout/edit-profile'
                      ? 'text-cyan-400'
                      : 'text-white hover:text-cyan-400'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm">Edit Profile</span>
                </button>
              )}

              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="flex items-center space-x-2 text-red-400 hover:text-red-300 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Modal */}
      <MessagesModal
        isOpen={isMessagesModalOpen}
        onClose={() => setIsMessagesModalOpen(false)}
      />
    </nav>
  );
};

export default DashboardNavbar;
